/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Página: Workforce » Operadores » Ficha de desempenho (ADR-0037 F3 v1)
 * - Consome GET /workforce/operators/{id}/performance/ (contrato F1, leitura pura).
 * - Hierarquia: PRODUTIVIDADE (destaque) -> por TIPO (gráfico+tabela) ->
 *   CONTEXTO (apoio) -> nota de futuros (D7).
 * - v1 SEM comparação vs equipa (isso é F2). Tempo médio GLOBAL não se mostra:
 *   não é derivável com exatidão do contrato (amostra de duração != concluidas).
 * ========================================================================== */
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import KpiCard from '@/components/KpiCard'
import { getOperatorPerformance } from '@/api/workforce'

const PERIODS = [7, 30, 90]
const LOW_SAMPLE = 5 // abaixo disto: marca "amostra baixa"

function fmtDur(s: number | null): string {
  if (s == null) return '—'
  const m = Math.floor(s / 60)
  const sec = Math.round(s % 60)
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`
}

const PANEL =
  'panel rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 p-4'

export default function OperatorPerformancePage() {
  const { id } = useParams<{ id: string }>()
  const operatorId = Number(id)
  const [days, setDays] = useState(30)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['operator-performance', operatorId, days],
    queryFn: () => getOperatorPerformance(operatorId, days),
    enabled: Number.isFinite(operatorId),
  })

  const status = isError
    ? (error as { response?: { status?: number } })?.response?.status
    : undefined
  const tipos = data ? Object.entries(data.por_tipo) : []
  const chartData = tipos.map(([tipo, m]) => ({
    tipo,
    segundos: m.tempo_medio_s ?? 0,
  }))

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* ── Cabeçalho: voltar + título + período ─────────────────────────── */}
      <div
        className={`${PANEL} flex flex-col md:flex-row md:items-center md:justify-between gap-3`}
      >
        <div>
          <Link
            to="/workforce/operators"
            className="text-sm text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
          >
            ← Operadores
          </Link>
          <h1 className="text-xl font-semibold">
            Desempenho{data ? ` — ${data.operator.nome}` : ''}
          </h1>
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setDays(p)}
              className={`btn btn-xs border ${
                days === p
                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                  : 'border-neutral-300 dark:border-neutral-700 bg-transparent text-neutral-700 dark:text-neutral-100'
              }`}
            >
              {p}d
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className={`${PANEL} text-neutral-500`}>A carregar…</div>
      )}
      {isError && (
        <div className={`${PANEL} text-red-600 dark:text-red-400`}>
          {status === 403
            ? 'Sem permissão — a ficha de desempenho é só para supervisores.'
            : 'Erro ao carregar o desempenho.'}
        </div>
      )}

      {data && (
        <>
          {/* Nota de amostra: sem dados (placeholder) OU amostra baixa */}
          {data.volume.total === 0 ? (
            <p className="text-sm text-neutral-500">
              Sem tarefas no período — dados de demonstração até haver operação
              real.
            </p>
          ) : data.volume.concluidas < LOW_SAMPLE ? (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Amostra baixa ({data.volume.concluidas} concluídas) — números
              indicativos.
            </p>
          ) : null}

          {/* ── 1. PRODUTIVIDADE (destaque) ──────────────────────────────── */}
          <section className="space-y-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Produtividade
            </h2>
            <div className="grid grid-cols-3 gap-3">
              <KpiCard
                label="Concluídas"
                value={data.volume.concluidas}
                hint={`em ${days} dias`}
              />
              <KpiCard label="Em curso" value={data.volume.em_curso} />
              <KpiCard label="Total atribuídas" value={data.volume.total} />
            </div>
          </section>

          {/* ── 2. POR TIPO: gráfico (resumo) + tabela (exato) ───────────── */}
          <section className={`${PANEL} space-y-3`}>
            <h2 className="text-base font-semibold">
              Tempo médio por tipo de tarefa
            </h2>
            {chartData.length === 0 ? (
              <p className="text-sm text-neutral-500">
                Sem tarefas por tipo no período.
              </p>
            ) : (
              <>
                <div className="h-64">
                  <ResponsiveContainer>
                    <BarChart
                      data={chartData}
                      margin={{ top: 8, right: 8, bottom: 8, left: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        className="stroke-neutral-200 dark:stroke-neutral-800"
                      />
                      <XAxis dataKey="tipo" />
                      <YAxis />
                      <Tooltip formatter={(v) => fmtDur(Number(v))} />
                      <Bar
                        dataKey="segundos"
                        fill="#2563eb"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-auto">
                  <table className="min-w-full table-auto text-sm">
                    <thead className="bg-neutral-50 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-100">
                      <tr>
                        <th className="text-left px-3 py-2">Tipo</th>
                        <th className="text-right px-3 py-2">N</th>
                        <th className="text-right px-3 py-2">Concluídas</th>
                        <th className="text-right px-3 py-2">Tempo médio</th>
                        <th className="text-right px-3 py-2">Mediana</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tipos.map(([tipo, m]) => (
                        <tr
                          key={tipo}
                          className="border-t border-neutral-200 dark:border-neutral-800"
                        >
                          <td className="px-3 py-2 font-medium">{tipo}</td>
                          <td className="px-3 py-2 text-right">{m.n}</td>
                          <td className="px-3 py-2 text-right">
                            {m.concluidas}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {fmtDur(m.tempo_medio_s)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {fmtDur(m.tempo_mediana_s)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          {/* ── 3. CONTEXTO / APOIO (peso visual menor) ──────────────────── */}
          <section className={`${PANEL} space-y-3`}>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Contexto
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <KpiCard label="Canceladas" value={data.volume.canceladas} />
              <KpiCard
                label="Responsividade"
                value={fmtDur(data.responsividade_media_s)}
                hint="aceite − atribuição"
              />
            </div>
            {Object.keys(data.por_status).length > 0 && (
              <div className="flex flex-wrap gap-2 text-sm">
                {Object.entries(data.por_status).map(([st, n]) => (
                  <span
                    key={st}
                    className="rounded-full border border-neutral-200 dark:border-neutral-700 px-2.5 py-0.5 text-neutral-600 dark:text-neutral-300"
                  >
                    {st}: {n}
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* ── 4. Nota de futuros (ADR-0037 D7 / F2) ────────────────────── */}
          <p className="text-xs text-neutral-500">
            Recusas de tarefa e linhas executadas entram quando o coletor os
            registar (ADR-0037 D7). Comparação vs média da equipa: F2.
          </p>
        </>
      )}
    </div>
  )
}
